import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import moment from 'moment';
import * as React from 'react';
import { useGetAdminPublicAuthKeys } from '../../../../queries';
import { LoadingSpinner } from '../../../../web/components/LoadingSpinner';
import { baseStyleSheet } from '../../../../web/styles/styles';
import { styleSheet } from './styles';

interface IProps {
	accountID: string;
}

const getConsumerDisplayName = (consumer: Api.PublicAPIConsumer): string => {
	switch (consumer) {
		case Api.PublicAPIConsumer.Zapier:
			return 'Zapier';
		case Api.PublicAPIConsumer.General:
			return 'General';
		case Api.PublicAPIConsumer.Unknown:
			return 'Unknown';
		default:
			return consumer || 'Unknown';
	}
};

export const AdminAPIKeys: React.FC<IProps> = ({ accountID }) => {
	const apiKeysQuery = useGetAdminPublicAuthKeys({
		enabled: true,
		refetchOnWindowFocus: false,
		accountID,
		meta: {
			toastErrorMessage: 'Error loading API keys',
			errorLogging: {
				category: 'AdminAPIKeys',
				action: 'GetAdminPublicAuthKeys-Error',
			},
		},
	});

	if (apiKeysQuery.isPending) {
		return <LoadingSpinner type='large' className={css(baseStyleSheet.absoluteCenter)} />;
	}

	if (!apiKeysQuery.data || apiKeysQuery.data.length === 0) {
		return (
			<div className={css(styleSheet.container)}>
				<header className={css(styleSheet.header)}>
					<h3 className={css(baseStyleSheet.breadcrumbTitle)}>API Keys</h3>
					<small>View API keys for integrations with third-party apps</small>
				</header>
				<div className={css(styleSheet.noDataContainer)}>
					<p>No API keys found for this account.</p>
				</div>
			</div>
		);
	}

	// Show all API keys to mirror user experience
	const allKeys = apiKeysQuery.data;

	return (
		<div className={css(styleSheet.container)}>
			<header className={css(styleSheet.header)}>
				<h3 className={css(baseStyleSheet.breadcrumbTitle)}>API Keys</h3>
				<p>View API keys for integrations with third-party apps</p>
			</header>

			{allKeys.length > 0 ? (
				<table className={css(styleSheet.tableContainer)}>
					<thead>
						<tr>
							<th className={css(styleSheet.tableHeaderCell)}>Description</th>
							<th className={css(styleSheet.tableHeaderCell)}>Integration</th>
							<th className={css(styleSheet.tableHeaderCell)}>Key</th>
							<th className={css(styleSheet.tableHeaderCell)}>Connection Status</th>
							<th className={css(styleSheet.tableHeaderCell)}>Added</th>
							<th className={css(styleSheet.tableHeaderCell)}>Last Used</th>
						</tr>
					</thead>
					<tbody>
						{allKeys.map((apiKey: Api.IAPIKeyReference) => {
							const lastUsedDate = apiKey.lastUsedDate ? moment(apiKey.lastUsedDate).format('MM/DD/YYYY') : 'Never';
							const consumerDisplay = getConsumerDisplayName(apiKey.consumer);

							// Determine connection status based on usage
							const isActive = apiKey.lastUsedDate && moment().diff(moment(apiKey.lastUsedDate), 'days') <= 30;
							const connectionStatus = apiKey.lastUsedDate ? (isActive ? 'Active' : 'Inactive') : 'Never Used';
							const statusClass = isActive
								? styleSheet.activeStatus
								: apiKey.lastUsedDate
									? styleSheet.inactiveStatus
									: styleSheet.neverUsedStatus;

							return (
								<tr key={`admin-auth-keys-${apiKey.blobId}`} className={css(styleSheet.tableRow)}>
									<td className={css(styleSheet.tableCell)}>{apiKey.description}</td>
									<td className={css(styleSheet.tableCell)}>
										<span className={css(styleSheet.consumerBadge)}>{consumerDisplay}</span>
									</td>
									<td className={css(styleSheet.tableCell)}>
										<span className={css(styleSheet.maskedKey)}>**** **** **** ****</span>
									</td>
									<td className={css(styleSheet.tableCell)}>
										<span className={css(statusClass)}>{connectionStatus}</span>
									</td>
									<td className={css(styleSheet.tableCell)}>{moment(apiKey.creationDate).format('MM/DD/YYYY')}</td>
									<td className={css(styleSheet.tableCell)}>{lastUsedDate}</td>
								</tr>
							);
						})}
					</tbody>
				</table>
			) : (
				<div className={css(styleSheet.noDataContainer)}>
					<p>No API keys found for this account.</p>
				</div>
			)}
		</div>
	);
};
